import React, { useEffect, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  Icon,
  Text,
} from '@chakra-ui/react';
import {
  MdWarningAmber,
  MdInfoOutline,
  MdHelpOutline,
  MdDeleteOutline,
} from 'react-icons/md';
import { subscribeConfirm } from './confirmStore';

const VARIANTS = {
  danger: {
    icon: MdDeleteOutline,
    color: 'red.500',
    bg: 'red.50',
    confirmScheme: 'red',
  },
  warning: {
    icon: MdWarningAmber,
    color: 'orange.500',
    bg: 'orange.50',
    confirmScheme: 'orange',
  },
  info: {
    icon: MdInfoOutline,
    color: 'blue.500',
    bg: 'blue.50',
    confirmScheme: 'blue',
  },
  question: {
    icon: MdHelpOutline,
    color: 'brand.500',
    bg: 'brand.50',
    confirmScheme: 'brand',
  },
};

const ConfirmProvider = () => {
  const [state, setState] = useState(null);
  const cancelRef = useRef(null);

  useEffect(() => {
    return subscribeConfirm(({ options, resolve }) => {
      setState({ options, resolve });
    });
  }, []);

  const close = result => {
    state?.resolve?.(result);
    setState(null);
  };

  if (!state) return null;

  const {
    title = 'Bạn có chắc chắn?',
    description,
    confirmText = 'Xác nhận',
    cancelText = 'Huỷ',
    variant = 'question',
    isLoading = false,
  } = state.options;

  const meta = VARIANTS[variant] || VARIANTS.question;

  return (
    <AlertDialog
      isOpen
      leastDestructiveRef={cancelRef}
      onClose={() => close(false)}
      isCentered
      motionPreset="slideInBottom"
    >
      <AlertDialogOverlay bg="blackAlpha.600" backdropFilter="blur(2px)">
        <AlertDialogContent borderRadius="lg" overflow="hidden" mx={4}>
          <AlertDialogHeader pb={2}>
            <Flex alignItems="center" gap={3}>
              <Flex
                bg={meta.bg}
                color={meta.color}
                w="40px"
                h="40px"
                borderRadius="full"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
              >
                <Icon as={meta.icon} boxSize={5} />
              </Flex>
              <Text fontSize="lg" fontWeight="600">
                {title}
              </Text>
            </Flex>
          </AlertDialogHeader>

          {description && (
            <AlertDialogBody pt={0} pl={16}>
              <Box fontSize="sm" color="gray.600" whiteSpace="pre-wrap">
                {description}
              </Box>
            </AlertDialogBody>
          )}

          <AlertDialogFooter gap={2}>
            <Button
              ref={cancelRef}
              onClick={() => close(false)}
              variant="ghost"
              isDisabled={isLoading}
            >
              {cancelText}
            </Button>
            <Button
              colorScheme={meta.confirmScheme}
              onClick={() => close(true)}
              isLoading={isLoading}
            >
              {confirmText}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export default ConfirmProvider;
